import { useEffect, useRef, useState } from 'react';
import Button from '@srm/ui/button';
import type { IAttachmentChange } from '@services/attachmentChanges/attachmentChanges.types';
import './AttachmentChangeToast.scss';

interface IAttachmentChangeToastProps {
    changes: IAttachmentChange[];
    onShow: () => void;
    onExpire: () => void;
}

export default function AttachmentChangeToast({ changes, onShow, onExpire }: IAttachmentChangeToastProps) {
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [hidden, setHidden] = useState(document.visibilityState === 'hidden');
    const remaining = useRef(8000);
    useEffect(() => {
        const update = () => setHidden(document.visibilityState === 'hidden');
        document.addEventListener('visibilitychange', update);
        return () => document.removeEventListener('visibilitychange', update);
    }, []);
    useEffect(() => {
        if (hovered || focused || hidden) return;
        const start = performance.now();
        const timer = setTimeout(onExpire, remaining.current);
        return () => {
            clearTimeout(timer);
            remaining.current = Math.max(0, remaining.current - (performance.now() - start));
        };
    }, [hovered, focused, hidden, onExpire]);
    const single = changes.length === 1 ? changes[0] : undefined;
    const added = changes.filter((change) => change.kind === 'added').length;
    return (
        <div
            className="attachment-change-toast"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => setFocused(true)}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
            }}
        >
            <div role="status" aria-live="polite" aria-atomic="true">
                <strong>
                    {single
                        ? single.kind === 'added'
                            ? 'Добавлен файл'
                            : 'Файл обновлён'
                        : 'Файлы документа обновлены'}
                </strong>
                <div className="attachment-change-toast__message">
                    {single ? single.item.fileName : `Добавлено: ${added}. Обновлено: ${changes.length - added}.`}
                </div>
            </div>
            <Button variant="text" size="sm" onClick={onShow}>
                {single ? 'Показать файл' : 'Показать изменения'}
            </Button>
        </div>
    );
}
